import { Box, keyframes } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { forwardRef } from 'react';
import DesignSystem from '../../designSystem';
import { MOBILE_BOTTOM_CHROME } from '../layout/BottomNavigation';
import FeedSkeleton from './FeedSkeleton';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.9; }
`;

interface FeedLoadMoreFooterProps {
  loading: boolean;
  skeletonCount?: number;
}

const FeedLoadMoreFooter = forwardRef<HTMLDivElement, FeedLoadMoreFooterProps>(
  function FeedLoadMoreFooter({ loading, skeletonCount = 4 }, ref) {
    return (
      <Box
        ref={ref}
        role="status"
        aria-live="polite"
        aria-busy={loading}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.25,
          pt: 1.5,
          pb: loading
            ? { xs: `calc(${MOBILE_BOTTOM_CHROME}px + env(safe-area-inset-bottom, 0px) + 24px)`, md: 3 }
            : 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 36,
            animation: `${fadeUp} 0.35s ${DesignSystem.motion.easing} both`,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: DesignSystem.colors.surface,
              border: `1px solid ${DesignSystem.colors.border}`,
              color: DesignSystem.colors.primary,
              boxShadow: DesignSystem.shadows.sm,
              animation: loading ? 'none' : `${pulse} 2.2s ease-in-out infinite`,
            }}
          >
            <RefreshRoundedIcon
              sx={{
                fontSize: 20,
                animation: loading ? `${spin} 0.85s linear infinite` : 'none',
              }}
            />
          </Box>
        </Box>

        {loading && (
          <Box
            sx={{
              animation: `${fadeUp} 0.3s ${DesignSystem.motion.easing} both`,
            }}
          >
            <FeedSkeleton count={skeletonCount} />
          </Box>
        )}
      </Box>
    );
  }
);

export default FeedLoadMoreFooter;
